import type {
  ProductTradeTermDraft,
  ProductTradeTermSnapshot,
  TradeTermSourceType,
  VendorTradeTerms,
} from './types';

export const PRODUCT_TRADE_TERM_SOURCE_LABELS: Record<TradeTermSourceType, string> = {
  VENDOR_APPLICATION: '新廠商申請',
  VENDOR_MASTER: '廠商主檔',
  PRODUCT_SPECIAL: '本商品特殊交易條件',
};

export const PRODUCT_TRADE_TERM_SPECIAL_STATUS_LABELS = {
  NONE: '沿用廠商交易條件',
  PENDING_BUYER_REVIEW: '待採購確認',
  ACCEPTED: '採購已接受',
  REJECTED: '採購拒絕，沿用廠商條件',
  REVISION_REQUIRED: '要求廠商重新提出',
} as const;

export type ProductTradeTermSpecialStatus = keyof typeof PRODUCT_TRADE_TERM_SPECIAL_STATUS_LABELS;

export function normalizeSpecialTradeTermsFlag(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    (typeof value === 'string' && value.trim().toLowerCase() === 'true')
  );
}

export function normalizeProductTradeTermsPayload(
  value: unknown,
): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const terms = value as Record<string, unknown>;
  return {
    ...terms,
    special_trade_terms: normalizeSpecialTradeTermsFlag(terms.special_trade_terms),
  };
}

export function shouldApplyAutosaveResponse(input: {
  responseSequence: number;
  latestSequence: number;
  startedRevision: number;
  currentRevision: number;
}): boolean {
  return (
    input.responseSequence === input.latestSequence &&
    input.startedRevision === input.currentRevision
  );
}

export function hasQuantifiedInformation(value: string): boolean {
  return /\d/.test(value);
}

export function normalizeTradeTermConditions(value: string[] | undefined): string[] {
  return [...new Set((value ?? []).map((item) => item.trim()).filter(Boolean))];
}

export function specialTermsEnabled(value: ProductTradeTermDraft | null | undefined): boolean {
  return normalizeSpecialTradeTermsFlag(value?.special_trade_terms);
}

export function missingVendorTradeTerms(value: VendorTradeTerms | null | undefined): string[] {
  if (!value) return ['廠商交易條件'];
  const missing: string[] = [];
  if (!value.payment_term) missing.push('付款條件');
  if (
    value.payment_term === 'MONTHLY' &&
    (!value.monthly_payment_days || value.monthly_payment_days <= 0)
  )
    missing.push('月結天數');
  if (!value.payment_method) missing.push('付款方式');
  if (!value.return_scope) missing.push('退貨適用地點');
  if (!value.return_handling_method) missing.push('退換貨方式');
  if (!value.return_conditions.length) missing.push('退換貨成立條件');
  if (!value.return_description) missing.push('退換貨詳細說明');
  if (!value.return_freight_responsibility) missing.push('退換貨運費負擔');
  return missing;
}

export function validateSpecialTradeTerms(
  value:
    | {
        special_trade_terms?: boolean | number;
        special_payment_term?: string;
        special_monthly_payment_days?: number | null;
        special_payment_method?: string;
        special_return_scope?: string;
        special_return_handling_method?: string;
        special_return_conditions?: string[];
        special_return_deadline?: string;
        special_return_description?: string;
        special_return_freight_responsibility?: string;
        special_reason?: string;
      }
    | ProductTradeTermDraft
    | null
    | undefined,
): string[] {
  if (!value) return [];
  if (!normalizeSpecialTradeTermsFlag(value.special_trade_terms)) return [];
  const missing: string[] = [];
  if (!value.special_payment_term?.trim()) missing.push('特殊付款條件');
  if (
    value.special_payment_term === 'MONTHLY' &&
    (!value.special_monthly_payment_days || value.special_monthly_payment_days <= 0)
  )
    missing.push('特殊月結天數');
  if (!value.special_payment_method?.trim()) missing.push('特殊付款方式');
  if (!value.special_return_scope?.trim()) missing.push('特殊退貨適用地點');
  if (!value.special_return_handling_method?.trim()) missing.push('特殊退換貨方式');
  if (!(value.special_return_conditions ?? []).length) missing.push('特殊退換貨成立條件');
  if (!value.special_return_description?.trim()) missing.push('特殊退換貨詳細說明');
  if (!value.special_return_freight_responsibility?.trim()) missing.push('特殊退換貨運費負擔');
  if (!value.special_reason?.trim()) missing.push('特殊交易條件原因');
  return missing;
}

export function effectiveProductTradeTerms(
  value: ProductTradeTermSnapshot | ProductTradeTermDraft,
): {
  payment_term: string;
  monthly_payment_days: number | null;
  payment_method: string;
  return_scope: string;
  return_handling_method: string;
  return_conditions: string[];
  return_description: string;
  return_freight_responsibility: string;
  source_type: TradeTermSourceType;
  source_id: string;
  special_status: ProductTradeTermSpecialStatus;
} {
  const useSpecial = specialTermsEnabled(value) && value.special_status === 'ACCEPTED';
  return {
    payment_term:
      useSpecial && value.special_payment_term ? value.special_payment_term : value.payment_term,
    monthly_payment_days:
      useSpecial && value.special_monthly_payment_days !== null
        ? value.special_monthly_payment_days
        : value.monthly_payment_days,
    payment_method:
      useSpecial && value.special_payment_method
        ? value.special_payment_method
        : value.payment_method,
    return_scope:
      useSpecial && value.special_return_scope ? value.special_return_scope : value.return_scope,
    return_handling_method:
      useSpecial && value.special_return_handling_method
        ? value.special_return_handling_method
        : value.return_handling_method,
    return_conditions:
      useSpecial && value.special_return_conditions.length
        ? normalizeTradeTermConditions(value.special_return_conditions)
        : normalizeTradeTermConditions(value.return_conditions),
    return_description:
      useSpecial && value.special_return_description
        ? value.special_return_description
        : value.return_description,
    return_freight_responsibility:
      useSpecial && value.special_return_freight_responsibility
        ? value.special_return_freight_responsibility
        : value.return_freight_responsibility,
    source_type: useSpecial ? 'PRODUCT_SPECIAL' : value.source_type,
    source_id: useSpecial ? value.id : value.source_id,
    special_status: value.special_status,
  };
}
